class ProductConfigurator extends (customElements.get('drawer-element') || HTMLElement) {
  constructor() {
    super();

    this.onOptionChangeListener = this.optionChange.bind(this);
    this.onOpenInfoPanel = this.openInfoPanel.bind(this);
    this.onCloseInfoPanel = this.closeInfoPanel.bind(this);
    this.onSubmitForm = this.submitForm.bind(this);
    this.onResetOptions = this.resetOptions.bind(this);
  }

  get cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  get focusElement() {
    return this.querySelector('.drawer__close') || super.focusElement;
  }

  connectedCallback() {
    // The registry fallback superclass is a plain HTMLElement with none of
    // DrawerElement's methods, so every super call checks first; a visitor
    // whose theme.js failed gets a quiet inert drawer, not a new error.
    // The same failure leaves theme.utils and theme.pubsub missing, so
    // init and event wiring are skipped outright in that state
    if (typeof super.connectedCallback === 'function') super.connectedCallback();
    if (!window.theme || !theme.utils) return;

    this.setup();
  }

  // The builder is wired from the drawer's contents, which can arrive after
  // the element: the parser can connect it before its children, and a copy
  // of the page made one node at a time connects it empty. Wiring waits for
  // the submit button and the info panel's close button (the last markup it
  // reads), so the builder is never half wired. whenDescendantsReady lives
  // in theme.js; without it, wire what is there now
  setup() {
    const waitFor = typeof whenDescendantsReady === 'function' ? whenDescendantsReady : (element, selectors, callback) => {
      if (selectors.every(selector => element.querySelector(selector))) callback();
      return () => {};
    };

    this.stopWaitingForContents?.();
    this.stopWaitingForContents = waitFor(this, ['[data-submit]', '[data-close-panel]'], () => {
      this.init();
      this.attachEvents();
      this.watchGifts();
    });
  }

  disconnectedCallback() {
    if (typeof super.disconnectedCallback === 'function') super.disconnectedCallback();

    this.stopWaitingForContents?.();

    // Not wired yet (no contents, or no theme globals): nothing to undo
    if (!this.submitButton) return;

    this.options.forEach(option => option.querySelector('input[type="checkbox"]')?.removeEventListener('change', this.onOptionChangeListener));

    this.infoPanelTriggers.forEach(button => button.removeEventListener('click', this.onOpenInfoPanel));

    this.infoPanelCloseButton.forEach(button => button.removeEventListener('click', this.onCloseInfoPanel));

    this.submitButton.removeEventListener('click', this.onSubmitForm);

    this.resetButton = this.querySelector('[data-reset]');
    if (this.resetButton) {
      this.resetButton.addEventListener('click', this.onResetOptions);
    }

    // Opening and closing move the drawer to <body> and back, which lands
    // here with it still on the page. The variant subscription stays put
    // for that (pick-a-day, subscribed later, reads the variant set here)
    // and only goes when the drawer leaves the page
    if (!this.isConnected) {
      this.variantChangeUnsubscriber?.();
      this.variantChangeUnsubscriber = null;
    }
  }

  resetOptions() {
    this.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      if (checkbox.checked) {
        checkbox.checked = false;
        checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });

    this.querySelectorAll('input[type="radio"]').forEach(radio => {
      radio.checked = false;
      radio.dispatchEvent(new Event("change", { bubbles: true }));
    });

    // Starting again is the drawer as it arrived, and the gift arrives ticked
    this.querySelectorAll('[data-gift] input[type="checkbox"]').forEach(checkbox => {
      checkbox.checked = true;
      checkbox.dispatchEvent(new Event("change", { bubbles: true }));
    });

    this.querySelectorAll('[data-section]').forEach(section => {
      const labelSelectedValue = section.querySelector('[data-selected-option]');
      const imageTag = section.querySelector('[data-media]');
      const infoBoxTrigger = this.sectionInfoTrigger(section);
      const hasSwatchOption = section.querySelector('[data-swatch-option]');

      if (labelSelectedValue) labelSelectedValue.textContent = '';
      if (imageTag) imageTag.classList.add('hidden');

      if (infoBoxTrigger && hasSwatchOption) {
        infoBoxTrigger.classList.add('hidden');
      }
    });

    this.updatePrice();
    this.syncRequirements();
  }

  attachEvents() {
    this.options.forEach(option => option.querySelector('input[type="checkbox"]')?.addEventListener('change', this.onOptionChangeListener));

    this.infoPanelTriggers.forEach(button => button.addEventListener('click', this.onOpenInfoPanel));

    this.infoPanelCloseButton.forEach(button => button.addEventListener('click', this.onCloseInfoPanel));

    this.submitButton.addEventListener('click', this.onSubmitForm);

    // One subscription for the drawer's life: every open and close runs
    // connectedCallback again and must not add another
    if (!this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange,
        this.variantChange.bind(this)
      );
    }
  }

  hide() {
    if (typeof super.hide === 'function') super.hide();

    this.closeInfoPanel();

    // A dismissed drawer takes any post-add panel with it; otherwise the
    // absolute overlay survives into the next open and covers a drawer
    // that can no longer be used
    const nudge = this.querySelector('.pcfg-nudge');
    if (nudge) {
      nudge.remove();
      this.serviceNudgeShown = false;
    }
  }

  init() {
    this.variantsOptions = this.querySelector('[data-variants-options]');
    this.sections = this.querySelectorAll('[data-section]');
    this.options = this.querySelectorAll('[data-option]');
    this.infoPanelTriggers = this.querySelectorAll('[data-target-tab]');
    this.infoPanel = this.querySelector('[data-info-panel]');
    this.infoPanelCloseButton = this.querySelectorAll('[data-close-panel]');
    this.submitButton = this.querySelector('[data-submit]');
    this.itemsObject = {};
    this.activeVariant = this.activeVariant ? this.activeVariant : JSON.parse(this.dataset.activeVariant);

    this.exclusionsMap = new Map();
    this.requiredMap = new Map();
    this.collectionMap = new Map();

    this.options.forEach(option => {
      const optionExclusionGroups = option.getAttribute('data-exclusion-groups');
      const optionRequiredWith = option.getAttribute('data-required-with');
      const optionCollections = option.getAttribute('data-collections');

      if (optionExclusionGroups) {
        const exclusionGroups = optionExclusionGroups.split(',');

        exclusionGroups.forEach((group) => {
          this.exclusionsMap.set(group, (this.exclusionsMap.get(group) ?? []).concat(option));
        });
      }

      if (optionCollections) {
        const collections = optionCollections.split(',');

        collections.forEach((collection) => {
          this.collectionMap.set(collection, (this.collectionMap.get(collection) ?? []).concat(option));
        });
      }

      if (optionRequiredWith) {
        const requiredWithGroups = optionRequiredWith.split(',');

        requiredWithGroups.forEach((group) => {
          this.requiredMap.set(group, (this.requiredMap.get(group) ?? []).concat(option));
        });
      }
    })

    this.syncRequirements();
  }

  // Free gift with purchase (snippets/free-gift-offer.liquid). Shopify can
  // serve this page from its cache after the offer has ended, so the end
  // time rides on the markup and the gift is dropped here at that moment.
  watchGifts() {
    (this.giftTimers || []).forEach(timer => window.clearTimeout(timer));
    this.giftTimers = [];

    this.querySelectorAll('[data-gift]').forEach(option => {
      const left = parseInt(option.dataset.giftUntil) - Date.now();

      if (!(left > 0)) {
        this.endGift(option);
      } else if (left < 2147483647) {
        this.giftTimers.push(window.setTimeout(() => this.endGift(option), left));
      }
    });
  }

  // Back to an ordinary paid option: unticked, priced and counted
  endGift(option) {
    if (!option.hasAttribute('data-gift')) return;

    option.removeAttribute('data-gift');
    option.querySelectorAll('[data-gift-free]').forEach(el => el.remove());
    option.querySelectorAll('[data-gift-paid]').forEach(el => { el.hidden = false; });

    const checkbox = option.querySelector('input[type="checkbox"]');

    if (checkbox && checkbox.checked) {
      checkbox.checked = false;
      checkbox.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  // The gift is only free if the automatic discount priced it at £0. When
  // the basket says otherwise the shopper is told now, not at checkout.
  // option is the gift as it was sent, since the offer can end mid add.
  giftNotice(cart, option) {
    if (!option || !cart || !Array.isArray(cart.items)) return '';

    const gifts = cart.items.filter(item => String(item.variant_id) === String(option.dataset.id));
    const lines = gifts.filter(item => item.properties && item.properties._parentKey === option.dataset.key);
    const charged = lines.reduce((total, item) => total + item.final_line_price, 0);

    if (lines.length && charged === 0) return '';

    const name = this.optionTitle(option);

    if (!lines.length) {
      // Not linked, but a free one is in the cart all the same
      if (gifts.some(item => item.final_line_price === 0)) return '';
      return `Your free ${name} didn’t make it into your cart. Call us on 01636\u00a0821\u00a0135 before you check out and we will add it.`;
    }

    const moneyFormat = theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat;
    const price = theme.Currency.formatMoney(charged, moneyFormat);

    // One free per sauna: another in the cart, linked or not, took it
    if (gifts.some(item => !lines.includes(item) && item.final_line_price === 0)) {
      return `You already have a free ${name} in your cart, so this one is charged at ${price}. Remove one of them if you only want the free one.`;
    }

    return `We couldn’t apply your free ${name}, so your cart is charging ${price} for it. Remove it from your cart if you don’t want to pay for it, or call us on 01636\u00a0821\u00a0135 before you check out and we will put it right.`;
  }

  variantChange({ data }) {
    this.activeVariant = data.variant;
    this.toggleOptionsVisibility();
    this.updateButtons();
    this.syncRequirements();
  }

  updateButtons() {
    const hasVisibilityOption = Array.from(this.options).some(option => !option.classList.contains('hidden'));
    const buttonsWrapper = document.querySelector('[data-buy-buttons]');

    if (!buttonsWrapper) return;

    const configuratorButton = buttonsWrapper.querySelector('[data-configurator-button]');
    const defaultButtons = buttonsWrapper.querySelector('[data-default-buttons]');

    if (!configuratorButton || !defaultButtons) return;

    configuratorButton?.classList?.toggle('hidden', !hasVisibilityOption);
    defaultButtons?.classList?.toggle('hidden', hasVisibilityOption);
  }

  toggleOptionsVisibility() {
    this.options.forEach(option => {
      const checkbox = option.querySelector('input[type="checkbox"]');
      const price = parseInt(option.dataset.price);
      const parentVariantId = option.dataset.parentVariantId;

      option.classList.toggle('hidden', parentVariantId != this.activeVariant.id);

      if (checkbox && parentVariantId != this.activeVariant.id && price != 0 && !option.hasAttribute('data-gift')) {
        checkbox.checked = false;

        checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
  }

  optionChange(event) {
    const target = event.target;
    const option = target.closest('[data-option]');

    this.handleOptionExclusions(target, option);
    this.handleOptionRequired(target, option);
    this.handleOptionExcludes(target, option);

    if (target.closest('[data-swatch-option]')) {
      this.updateSwatchSection(target, option, event);
    }

    this.updatePrice();
    this.syncRequirements();
  }

  updateSwatchSection(target, option, event) {
    const section = target.closest('[data-section]');

    if (!section) return;

    const imageTag = section.querySelector('[data-media]');
    const labelSelectedValue = section.querySelector('[data-selected-option]');
    const selectedValuePriceContainer = section.querySelector('[data-selected-option-price]');
    const infoBoxTrigger = this.sectionInfoTrigger(section);

    const isTriggeredByExclusion = event.detail?.triggeredByExclusion || false;

    if (!isTriggeredByExclusion) {
      // Per-section single-select, scoped so a "one merged card" step can hold
      // BOTH the mutually-exclusive finishes (Premium Royal Exterior, Wood
      // Stain and Paint, …) AND an independent optional extra (Wood Preserver)
      // in the same swatch card. Finishes that share an exclusion group are
      // already made one-of across the whole configurator by
      // handleOptionExclusions, so we must not also sweep them here; and a
      // group-less extra must never clear them. Rule: a group-less swatch only
      // clears other group-less swatches; a grouped swatch leaves exclusion to
      // handleOptionExclusions. Products with no exclusion groups (every legacy
      // swatch step) are all group-less, so this behaves exactly as before.
      const targetGroups = (option?.getAttribute('data-exclusion-groups') || '').split(',').filter(Boolean);

      if (targetGroups.length === 0) {
        const activeSwatches = section.querySelectorAll('[data-swatch-option] input[type="checkbox"]:checked');

        activeSwatches?.forEach(checkbox => {
          if (target.checked !== true || checkbox === target || checkbox.checked !== true) return;

          const otherGroups = (checkbox.closest('[data-option]')?.getAttribute('data-exclusion-groups') || '').split(',').filter(Boolean);

          if (otherGroups.length === 0) {
            checkbox.checked = false;
            checkbox.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });
      }
    }

    const activeSwatch = section.querySelector('[data-swatch-option] input[type="checkbox"]:checked');

    if (!activeSwatch) {
      const visibleSwatches = Array.from(section.querySelectorAll('[data-swatch-option]:not(.hidden)'));

      labelSelectedValue.textContent = '';
      imageTag.classList.add('hidden');
      infoBoxTrigger.classList.add('hidden');

      const prices = visibleSwatches.map((s) => parseInt(s.dataset.price)).filter((n) => !isNaN(n));

      if (prices.length) {
        // Cheapest visible option, labelled "from" when the card mixes
        // prices (a merged finishes card holds the stain family next to
        // the dearer Protek family); single-price sections read as before.
        const price = Math.min(...prices);
        const mixed = new Set(prices).size > 1;

        selectedValuePriceContainer.textContent = `${mixed ? 'from ' : ''}+${theme.Currency.formatMoney(price, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat)}`;
      } else {
        selectedValuePriceContainer.textContent = '';
      }

    } else {
      const parentActiveSwatch = activeSwatch.closest('[data-swatch-option]');
      const price = parseInt(parentActiveSwatch.dataset.price);

      labelSelectedValue.textContent = parentActiveSwatch.dataset.name;
      imageTag.src = parentActiveSwatch.dataset.image;
      imageTag.classList.remove('hidden');

      infoBoxTrigger.dataset.targetTab = parentActiveSwatch.dataset.infoTab;
      infoBoxTrigger.classList.remove('hidden');

      selectedValuePriceContainer.textContent = `+${theme.Currency.formatMoney(price, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat)}`;
    }
  }

  handleOptionExclusions(target, option) {
    const optionExclusionGroups = option.getAttribute('data-exclusion-groups');

    if (!optionExclusionGroups || !target.checked) return;

    const exclusionGroups = optionExclusionGroups.split(',');

    exclusionGroups.forEach(group => {
      const groups = this.exclusionsMap.get(group);

      groups.forEach((groupOption) => {
        if (groupOption !== option) {
          const groupCheckbox = groupOption.querySelector('input[type="checkbox"]');

          if (!groupCheckbox || !groupCheckbox.checked) return;

          groupCheckbox.checked = false;

          groupCheckbox.dispatchEvent(new CustomEvent("change", {
            bubbles: true,
            detail: {
              triggeredByExclusion: true
            }
          }));
        }
      });
    });
  }

  // Required-with rules (waltons.required_with_products): an option that goes
  // with a selection from each of its listed collections, e.g. Wooden Base
  // Home Installation with a Wooden Base plus Home Installation (Building
  // Only). Worked out from what is ticked and visible (so only this size),
  // never from running counts.
  requirementTriggers(requiredOption) {
    const groups = (requiredOption.getAttribute('data-required-with') || '').split(',').filter(Boolean);
    if (!groups.length) return null;

    const triggers = [];

    for (const group of groups) {
      const picked = (this.collectionMap.get(group) ?? []).filter(candidate =>
        candidate !== requiredOption &&
        !candidate.classList.contains('hidden') &&
        candidate.querySelector('input[type="checkbox"]')?.checked
      );

      if (!picked.length) return null;

      picked.forEach(candidate => {
        if (!triggers.includes(candidate)) triggers.push(candidate);
      });
    }

    return triggers;
  }

  // Only a free required option is ticked for the shopper. A paid one never
  // is: a pre-ticked paid extra is not the shopper's choice, so the build is
  // held back with a notice instead (syncRequirements, submitForm).
  handleOptionRequired(target, option) {
    const optionCollections = option.getAttribute('data-collections');

    if (!optionCollections || this.requiredMap.size === 0) return;

    const affected = new Set();

    optionCollections.split(',').forEach(collection => {
      (this.requiredMap.get(collection) ?? []).forEach(requiredOption => affected.add(requiredOption));
    });

    affected.forEach(requiredOption => {
      if (parseInt(requiredOption.dataset.price)) return;

      const checkbox = requiredOption.querySelector('input[type="checkbox"]');
      if (!checkbox) return;

      const met = !requiredOption.classList.contains('hidden') && Boolean(this.requirementTriggers(requiredOption));

      // Ticked once, when its rule becomes met. It is free, so a shopper who
      // unticks it afterwards is left alone (putting it back fought exclusion
      // groups and data-excludes for no gain).
      if (met && !requiredOption.requirementLocked) {
        requiredOption.requirementLocked = true;
        this.updateReadOnlyState(requiredOption, 1);

        if (!checkbox.checked) {
          checkbox.checked = true;
          checkbox.dispatchEvent(new Event("change", { bubbles: true }));
        }
      } else if (!met && requiredOption.requirementLocked) {
        requiredOption.requirementLocked = false;
        this.updateReadOnlyState(requiredOption, -1);
      }
    });
  }

  // Paid required options for this size whose triggers are all ticked but
  // which the shopper has not added.
  unmetRequirements() {
    const unmet = [];

    this.requiredMap.forEach(list => list.forEach(requiredOption => {
      if (unmet.some(entry => entry.option === requiredOption)) return;
      if (requiredOption.classList.contains('hidden') || !parseInt(requiredOption.dataset.price)) return;

      const checkbox = requiredOption.querySelector('input[type="checkbox"]');
      if (checkbox && checkbox.checked) return;

      const triggers = this.requirementTriggers(requiredOption);
      if (triggers) unmet.push({ option: requiredOption, triggers, canAdd: Boolean(checkbox) });
    }));

    return unmet;
  }

  optionTitle(option) {
    const title = option.querySelector('.wz-opt__title') || option.querySelector('label') || option;
    return (option.dataset.name || title.textContent || '').trim();
  }

  requirementMessage(entry) {
    // Product copy first (waltons.required_with_message on the required
    // option), then the theme's default for the only rule in use today.
    const custom = entry.option.getAttribute('data-required-message');
    const pattern = (entry.canAdd ? custom || this.dataset.requiredMessage : this.dataset.requiredMessageOos) || '';
    const name = this.optionTitle(entry.option).replace(/\s*\([^)]*\)\s*$/, '');

    return pattern.replace(/__REQUIRED__/g, name);
  }

  // One notice in every section holding the required option or one of its
  // triggers, so it shows on whichever step the shopper is on. The wizard
  // treats a step holding a notice as blocked.
  syncRequirements() {
    if (!this.requiredMap || this.requiredMap.size === 0) return;

    const unmet = this.unmetRequirements();

    // A refused add leaves its message in the footer; take it away once the
    // shopper has sorted it (a cart error from Shopify is left alone).
    if (!unmet.length && this.requirementError) {
      this.requirementError = null;
      this.handleErrorMessage();
    }

    const wanted = new Map();

    unmet.forEach(entry => {
      [entry.option, ...entry.triggers].forEach(member => {
        const section = member.closest('[data-section]');
        if (section && !wanted.has(section)) wanted.set(section, entry);
      });
    });

    this.querySelectorAll('.pcfg-required').forEach(notice => {
      const section = notice.closest('[data-section]');
      const entry = wanted.get(section);

      if (entry && notice.requirement === entry.option && notice.dataset.message === this.requirementMessage(entry)) {
        wanted.delete(section);
        return;
      }

      notice.remove();
    });

    const moneyFormat = theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat;
    // The wizard shows one step at a time, so each notice can announce itself;
    // the classic layout shows every section, so announce each rule once.
    const announced = new Set();

    wanted.forEach((entry, section) => {
      const anchor = section.querySelector('.section-options__body');
      if (!anchor) return;

      const message = this.requirementMessage(entry);
      const notice = document.createElement('div');
      notice.className = 'pcfg-required';
      if (this.hasAttribute('data-wizard') || !announced.has(entry.option)) {
        notice.setAttribute('role', 'alert');
        announced.add(entry.option);
      }
      notice.dataset.message = message;
      notice.requirement = entry.option;

      const text = document.createElement('p');
      text.className = 'pcfg-required__text';
      text.textContent = message;
      notice.appendChild(text);

      if (entry.canAdd) {
        const add = document.createElement('button');
        add.type = 'button';
        add.className = 'pcfg-required__add';
        add.textContent = (this.dataset.requiredAdd || 'Add for __PRICE__')
          .replace('__PRICE__', theme.Currency.formatMoney(parseInt(entry.option.dataset.price), moneyFormat));
        add.addEventListener('click', () => {
          const checkbox = entry.option.querySelector('input[type="checkbox"]');
          if (!checkbox || checkbox.checked) return;
          checkbox.checked = true;
          checkbox.dispatchEvent(new Event("change", { bubbles: true }));

          // The notice (and this button) is gone now; keep focus in the
          // drawer on the option just added, or this section's heading when
          // the option sits on another step.
          if (checkbox.getClientRects().length) {
            checkbox.focus({ preventScroll: true });
          } else {
            const heading = section.querySelector('.section-options__title');
            if (heading) {
              heading.setAttribute('tabindex', '-1');
              heading.focus({ preventScroll: true });
            }
          }
        });
        notice.appendChild(add);
      }

      anchor.parentElement.insertBefore(notice, anchor);
    });
  }

  handleOptionExcludes(target, option) {
    const optionExcludesGroup = option.getAttribute('data-excludes');

    if (optionExcludesGroup) {
      const exclusionGroups = optionExcludesGroup.split(',');

      exclusionGroups.forEach(exclusionGroup => {
        const exclusionGroupOptions = this.collectionMap.get(exclusionGroup);

        if (exclusionGroupOptions) {
          exclusionGroupOptions.forEach(exclusionGroupOption => {
            const groupOptionCheckbox = exclusionGroupOption.querySelector('input[type="checkbox"]');

            this.updateReadOnlyState(exclusionGroupOption, target.checked ? 1 : -1);

            if (target.checked && groupOptionCheckbox && groupOptionCheckbox.checked) {
              groupOptionCheckbox.checked = false;

              groupOptionCheckbox.dispatchEvent(new Event("change", { bubbles: true }));
            }
          })
        }
      });
    }
  }

  updateReadOnlyState(option, count) {
    const checkbox = option.querySelector('input[type="checkbox"]');

    if (!checkbox) return;

    if (!('readOnlyCount' in checkbox)) {
      checkbox.readOnlyCount = 0;
    }

    checkbox.readOnlyCount += count;

    if (checkbox.readOnlyCount > 0 && !checkbox.readOnly) {
      checkbox.readOnly = true;
    } else if (checkbox.readOnlyCount < 1 && checkbox.readOnly) {
      checkbox.readOnly = false;
    }
  }

  // The section's own "More information" trigger, i.e. the first one that
  // does not belong to an option card. Checkbox cards carry their own
  // trigger; in the wizard's merged finishes card the Wood Preserver cards
  // are moved above the swatch group's trigger, so the first match would be
  // a preserver card and its link would be retargeted at the ticked paint.
  sectionInfoTrigger(section) {
    return Array.from(section.querySelectorAll('[data-target-tab]')).find((el) => !el.closest('[data-option]')) || null;
  }

  openInfoPanel(event) {
    const target = event.target;
    const targetTabId = target.dataset.targetTab;

    event.preventDefault();

    // data-target-tab is empty until a swatch is chosen; querySelector('#')
    // (empty id) throws "'#' is not a valid selector", so bail before it.
    if (!targetTabId) return;

    const targetTab = this.querySelector(`#${targetTabId}`);
    const infoTabs = this.querySelectorAll('[data-info-panel] [data-info-tab]');

    if (!targetTab) return;

    this.infoPanel.setAttribute('open', true);

    infoTabs.forEach(infoTab => infoTab.classList.toggle('hidden', infoTab !== targetTab));
  }

  closeInfoPanel(event) {
    if (event) {
      event.preventDefault();
    }

    this.infoPanel.removeAttribute('open');
  }

  generateItems() {
    this.itemsObject = {
      items: []
    };

    const properties = { _key: this.dataset.parentKey };
    // Pre-order products (snippets/product-configurator.liquid): the same
    // visible Pre-order line the buy button adds, so the basket, checkout,
    // the order and its emails show the delivery time the customer saw.
    if (this.dataset.preorder) properties['Pre-order'] = this.dataset.preorder;

    this.itemsObject.items.push({
      id: this.activeVariant.id,
      quantity: 1,
      properties
    });

    const activeOptions = this.querySelectorAll('[data-option]:not(.hidden) input[type="checkbox"]:checked');

    activeOptions.forEach(activeOption => {
      const parent = activeOption.closest('[data-option]');

      this.itemsObject.items.push({
        id: parent.dataset.id,
        quantity: 1,
        properties: {
          _parentKey: parent.dataset.key
        }
      });
    });

    this.itemsObject.items.reverse();
  }

  // Pick-a-day (assets/pick-a-day.js): the lines this build would add, as
  // MGP items. The same selector as generateItems, so what is quoted is
  // what is added; options for other sizes can sit checked, hence
  // :not(.hidden). data-pad-sku is only rendered with pick-a-day on.
  padLines() {
    const lines = [];
    const buildSku = this.activeVariant && typeof this.activeVariant.sku === 'string' ? this.activeVariant.sku.trim() : '';
    if (buildSku) lines.push({ sku: buildSku, quantity: 1 });

    this.querySelectorAll('[data-option]:not(.hidden) input[type="checkbox"]:checked').forEach(activeOption => {
      const sku = activeOption.closest('[data-option]').dataset.padSku;
      if (sku) lines.push({ sku, quantity: 1 });
    });

    return lines;
  }

  updatePrice() {
    const activeOptions = this.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked');
    const totalPrice = Array.from(activeOptions).reduce(
      (accumulator, currentValue) => {
        const parent = currentValue.closest('[data-variant-option]') || currentValue.closest('[data-option]');
        if (!parent || parent.classList.contains('hidden')) return accumulator;
        // The gift goes to the basket at full price and the automatic
        // discount takes it to £0, so it adds nothing to what is charged
        if (parent.hasAttribute('data-gift')) return accumulator;

        const price = parseInt(parent.dataset.price);
        return accumulator + price;
      }, this.activeVariant.price);

    const moneyFormat = theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat;

    this.submitButton.querySelector('.btn-text').textContent = this.submitButton.dataset.textPattern.replace(
      '[money]',
      theme.Currency.formatMoney(totalPrice, moneyFormat)
    );

    // Finance messaging in the drawer tracks the running total (minor units)
    document.dispatchEvent(new CustomEvent('wizard:total', { detail: { total: totalPrice } }));
  }

  // Installation and painting carry the margin; when they exist for this
  // building and were left unticked, ask once before the add goes through.
  collectSkippedServices() {
    return Array.from(this.querySelectorAll('[data-checkbox-option]')).filter(option => {
      if (option.classList.contains('hidden') || option.hasAttribute('data-oos')) return false;
      const input = option.querySelector('input[type="checkbox"]');
      if (!input || input.checked) return false;
      if (!parseInt(option.dataset.price)) return false;
      if (this.completesPaidRequirement(option)) return false;
      const title = ((option.querySelector('.wz-opt__title') || option.querySelector('label') || option).textContent || '').trim();
      // Painting anywhere in the title; installation only near the front of
      // it, so a base with "Installation Included" mid-name stays out
      const installAt = title.search(/install/i);
      return /paint/i.test(title) || (installAt > -1 && installAt < 16);
    });
  }

  // A one tap add after the building is in the cart skips the drawer's
  // checks, so never offer an option that would complete a paid required
  // rule (e.g. Home Installation when a Wooden Base is in) without the
  // required option (Wooden Base Home Installation).
  completesPaidRequirement(candidate) {
    const collections = (candidate.getAttribute('data-collections') || '').split(',').filter(Boolean);

    return collections.some(collection => (this.requiredMap.get(collection) ?? []).some(requiredOption => {
      if (requiredOption === candidate || requiredOption.classList.contains('hidden')) return false;
      if (!parseInt(requiredOption.dataset.price)) return false;
      if (requiredOption.querySelector('input[type="checkbox"]')?.checked) return false;

      const groups = (requiredOption.getAttribute('data-required-with') || '').split(',').filter(Boolean);

      return groups.every(group => collections.includes(group) || (this.collectionMap.get(group) ?? []).some(other =>
        other !== requiredOption &&
        !other.classList.contains('hidden') &&
        other.querySelector('input[type="checkbox"]')?.checked
      ));
    }));
  }

  // Post-add, not a gate: the building is already in the cart when this
  // shows. A tick confirms the add, then the skipped services are offered
  // with one-tap adds of their own.
  showPostAddServices(giftNotice = '') {
    // Services are offered once; a gift that did not come through free is
    // said on every add
    const skipped = this.serviceNudgeShown ? [] : this.collectSkippedServices();
    if (!skipped.length && !giftNotice) return false;
    if (skipped.length) this.serviceNudgeShown = true;

    const moneyFormat = theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat;
    const host = this.querySelector('.drawer__inner') || this;
    const panel = document.createElement('div');
    panel.className = 'pcfg-nudge pcfg-nudge--added';
    const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
    const rows = skipped.map((option) => {
      const title = escapeHtml(((option.querySelector('.wz-opt__title') || option.querySelector('label') || option).textContent || '').trim().replace(/\s+/g, ' '));
      const price = theme.Currency.formatMoney(parseInt(option.dataset.price), moneyFormat);
      return '<div class="pcfg-nudge__row" data-service-row>' +
        '<span class="pcfg-nudge__row-title">' + title + '</span>' +
        '<span class="pcfg-nudge__row-price">+ ' + price + '</span>' +
        '<button type="button" class="pcfg-nudge__row-add" data-service-add="' + option.dataset.id + '">Add</button>' +
        '</div>';
    }).join('');
    panel.innerHTML =
      '<div class="pcfg-nudge__panel" role="status">' +
      '<div class="pcfg-nudge__tick" aria-hidden="true"><svg viewBox="0 0 52 52"><circle cx="26" cy="26" r="24" fill="none"/><path fill="none" d="M14 27l8 8 16-16"/></svg></div>' +
      '<p class="pcfg-nudge__title">Added to your cart</p>' +
      (giftNotice ? '<p class="pcfg-nudge__sub pcfg-nudge__gift" role="alert">' + escapeHtml(giftNotice) + '</p>' : '') +
      (skipped.length ? '<p class="pcfg-nudge__sub">Are you sure you don&rsquo;t want ' + (skipped.length > 1 ? 'these' : 'this') + ' with it?</p>' : '') +
      rows +
      '<button type="button" class="button button--primary pcfg-nudge__add" data-view-cart>View your cart</button>' +
      '</div>';
    host.appendChild(panel);

    panel.querySelectorAll('[data-service-add]').forEach(btn => {
      btn.addEventListener('click', () => {
        btn.disabled = true;
        btn.textContent = 'Adding';
        this.addServiceToCart(btn.getAttribute('data-service-add'))
          .then(() => {
            btn.textContent = 'Added';
            btn.closest('[data-service-row]').classList.add('is-added');
          })
          .catch(() => {
            btn.disabled = false;
            btn.textContent = 'Add';
          });
      });
    });
    panel.querySelector('[data-view-cart]').addEventListener('click', () => {
      panel.remove();
      this.hide();
      this.cartDrawer?.show();
    });
    return true;
  }

  addServiceToCart(variantId) {
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));
    const body = JSON.stringify({
      items: [{ id: parseInt(variantId), quantity: 1 }],
      sections: sectionsToBundle,
      sections_url: window.location.pathname
    });
    return fetch(`${theme.routes.cart_add_url}`, { ...theme.utils.fetchConfig('javascript'), body })
      .then(response => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) throw new Error(parsedState.description);
        const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
        cartJson['sections'] = parsedState['sections'];
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-configurator', cart: cartJson });
      });
  }

  submitForm() {
    // A paid option this build needs but the shopper has not added: stop
    // here and point at the notice rather than add an incomplete build.
    const unmet = this.unmetRequirements();

    if (unmet.length) {
      this.syncRequirements();
      this.requirementError = this.requirementMessage(unmet[0]);
      // Cleared first so a second press is announced again.
      this.handleErrorMessage();
      window.setTimeout(() => {
        if (this.requirementError) this.handleErrorMessage(this.requirementError);
      }, 50);
      this.dispatchEvent(new CustomEvent('configurator:requirements-unmet', {
        bubbles: true,
        detail: { option: unmet[0].option, triggers: unmet[0].triggers }
      }));
      return;
    }

    // Pick-a-day on (data-pad): save the chosen delivery day to the cart
    // first, so the add's drawer render already shows it. Capped at a few
    // seconds and never a gate: any failure adds exactly as before. The
    // guard stops a second tap adding twice while that runs. commitStep
    // hands back an undo for when the add then fails.
    if (this.hasAttribute('data-pad') && window.WaltonsPickADay) {
      if (this.padSubmitting) return;
      this.padSubmitting = true;
      this.submitButton.setAttribute('aria-busy', 'true');
      const release = () => { this.padSubmitting = false; };
      window.WaltonsPickADay.commitStep(this)
        .catch(() => {})
        .then((padUndo) => this.addToCart(padUndo))
        .then(release, release);
      return;
    }

    this.addToCart();
  }

  addToCart(padUndo) {
    this.generateItems();
    const giftOption = this.querySelector('[data-gift]:not(.hidden) input[type="checkbox"]:checked')?.closest('[data-option]');

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

    this.submitButton.setAttribute('aria-disabled', 'true');
    this.submitButton.setAttribute('aria-busy', 'true');

    const body = JSON.stringify({
      ...this.itemsObject,
      sections: sectionsToBundle,
      sections_url: window.location.pathname
    });

    this.handleErrorMessage();

    return fetch(`${theme.routes.cart_add_url}`, { ...theme.utils.fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          if (typeof padUndo === 'function') padUndo();
          this.handleErrorMessage(parsedState.description);

          document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
            detail: {
              errorMessage: parsedState.description
            }
          }));
          return;
        }
        padUndo = null;

        const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
        cartJson['sections'] = parsedState['sections'];

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-configurator', cart: cartJson });

        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: parsedState
          }
        }));

        if (this.showPostAddServices(this.giftNotice(cartJson, giftOption))) return;

        this.hide();

        this.cartDrawer?.show();
      })
      .catch((error) => {
        if (typeof padUndo === 'function') padUndo();
        console.log(error);
      })
      .finally(() => {
        this.submitButton.removeAttribute('aria-busy');
      });
  }

  handleErrorMessage(errorMessage = false) {
    this.errorMessage = this.querySelector('[data-error-message]');
    if (!this.errorMessage) return;

    this.errorMessage.toggleAttribute('hidden', !errorMessage);
    this.errorMessage.innerText = errorMessage || '';
  }
}

customElements.define('product-configurator', ProductConfigurator);
